import React from 'react'
import useScreenSize from 'hooks/useScreenSize'
import AlertFilters from './AlertFilters'
import * as S from './LegendStyles'
import { LegendItem, legendZone } from 'types/RuntimeHistory'
import IndoorFilters from './IndoorFilters'
import IndoorUnitFilters from './IndoorUnitFilters'
import ZoneFilters from './ZoneFilters'
import { Alert } from 'types/Alert'
interface ZoneSeries {
  name: string;
  series: Array<string> | null;
}

export interface SelectedLegends {
  series: Array<string> | null;
  zoneSeries: Array<ZoneSeries> | null;
  alerts: Array<string> | null;
}

export interface SelectedZone {
  seriesName: string;
  zoneIndex: number;
}

interface DisplayGroups {
  indoorUnit: boolean,
  outdoor: boolean,
  outdoorUnit: boolean
}

interface FilterListProps {
  zones: Array<Required<legendZone>>;
  displayGroups:DisplayGroups,
  indoorLegendItems: Array<Required<LegendItem>>;
  indoorUnitLegendItems: Array<Required<LegendItem>>;
  outdoorLegendItems: Array<Required<LegendItem>>;
  outdoorUnitLegendItems: Array<Required<LegendItem>>;
  alerts: Array<Alert>;
  selectedLegends: SelectedLegends;
  isControlHub: boolean;
  onZoneSelectionChanged: (zone: SelectedZone, isChecked: boolean) => void;
  onFilterSelectionChanged: (seriesName: string) => void;
  onIndoorSelectionChanged: (seriesName: string, isChecked: boolean) => void;
  onAlertsSelectionChanged: (seriesName: string) => void;
  selectedDataPoints: number;
  isHehm?: boolean;
}

const FilterList = ({
  zones,
  displayGroups,
  indoorLegendItems,
  indoorUnitLegendItems,
  outdoorLegendItems,
  outdoorUnitLegendItems,
  alerts,
  selectedLegends,
  onZoneSelectionChanged,
  onFilterSelectionChanged,
  onIndoorSelectionChanged,
  onAlertsSelectionChanged,
  selectedDataPoints,
  isHehm = false
}: FilterListProps) => {
  const screenSize = useScreenSize()
  const isMediumScreen = screenSize === 'medium'
  const hasMultipleZones = zones.length > 1
  const totalAvailableDataPoints = (hasMultipleZones ? indoorLegendItems.length * zones.length : (indoorLegendItems?.length || 0)) +
    (indoorUnitLegendItems?.length || 0) + (outdoorLegendItems?.length || 0) + (outdoorUnitLegendItems?.length || 0)
  const isFilterItemSelected = (legendItem: LegendItem): boolean => {
    return (
      selectedLegends.series &&
      selectedLegends.series.includes(legendItem.seriesName)
    ) ?? false
  }

  return (
    <S.List>
      <AlertFilters
        alerts={alerts}
        selectedLegends={selectedLegends}
        onAlertsSelectionChanged={onAlertsSelectionChanged}
        isHehm={isHehm}
      />
      {totalAvailableDataPoints > 20 &&
        <S.Typography>
          Select up to 20 data points below ({selectedDataPoints}/20)
        </S.Typography>
      }
      {hasMultipleZones && !isHehm ? (
        <ZoneFilters
          data={indoorLegendItems}
          selectedLegends={selectedLegends}
          zones={zones}
          onFilterSelectionChanged={onZoneSelectionChanged}
        />
      ) : (
        <S.ListGroup>
          {!isHehm &&
            <IndoorFilters
              data={indoorLegendItems}
              selectedLegends={selectedLegends}
              onFilterSelectionChanged={onIndoorSelectionChanged}
            />
          }
          <IndoorUnitFilters
            data={indoorUnitLegendItems}
            showSection={displayGroups.indoorUnit}
            isFilterItemSelected={isFilterItemSelected}
            onFilterSelectionChanged={onFilterSelectionChanged}
          />

        </S.ListGroup>
      )}
      {
        ((displayGroups.indoorUnit && hasMultipleZones) || displayGroups.outdoor || displayGroups.outdoorUnit) && (
          <S.ListGroup gap={isMediumScreen ? 1 : 0}>
            <IndoorUnitFilters
              data={indoorUnitLegendItems}
              showSection={displayGroups.outdoorUnit && hasMultipleZones}
              isFilterItemSelected={isFilterItemSelected}
              onFilterSelectionChanged={onFilterSelectionChanged}
            />
            {displayGroups.outdoor && (
            <S.NonZoneSection>
              <S.Typography>Outdoor</S.Typography>
              <S.CheckboxGroup>
                {outdoorLegendItems?.map((legendItem) => {
                  return (
                    <S.FilterCheckbox
                      key={legendItem.seriesName}
                      label={legendItem.label}
                      listId="outdoor"
                      checked={isFilterItemSelected(legendItem)}
                      onChange={() =>
                        onFilterSelectionChanged(legendItem.seriesName)
                      }
                    />
                  )
                })}
              </S.CheckboxGroup>
            </S.NonZoneSection>
            )}
            {displayGroups.outdoorUnit && (
            <S.NonZoneSection flexWidth={370}>
              <S.Typography>Outdoor Unit</S.Typography>
              <S.CheckboxGroup>
                {outdoorUnitLegendItems?.map((legendItem) => {
                  return (
                    <S.FilterCheckbox
                      key={legendItem.seriesName}
                      label={legendItem.label}
                      listId="outdoor_unit"
                      checked={isFilterItemSelected(legendItem)}
                      onChange={() =>
                        onFilterSelectionChanged(legendItem.seriesName)
                      }
                    />
                  )
                })}
              </S.CheckboxGroup>
            </S.NonZoneSection>
            )}
          </S.ListGroup>
        )
      }
    </S.List>
  )
}

export default FilterList
